import React, { useState, useEffect, useRef } from "react";
import { Table, Link2, Copy, Check, Settings2 } from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../modals/AuthContext";
import { useAuthModal } from "../modals/AuthModalContext";
import { useGoogleAuth } from "../modals/AuthContext";
import TrackInSheetsModal from "../modals/TrackInSheetsModal";
import {
  fetchUserMappings,
  findMappingForUrl,
  saveMetricsToUserSheet,
  formatSheetSaveError,
} from "../../utils/sheetSave";
import { trackEvent, AnalyticsEvents } from "../../utils/analytics";
import {
  getTestRunKey,
  getCachedShareUrl,
  setCachedShareUrl,
  copyShareUrl,
} from "../../utils/shareReportCache";

const MetricActions = ({ metrics, allMetrics }) => {
  const [trackModalOpen, setTrackModalOpen] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const copiedTimeoutRef = useRef(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [existingMapping, setExistingMapping] = useState(null);
  const { isAuthenticated, user, token } = useAuth();
  const { openAuthModal } = useAuthModal();
  const { googleConnected, googleAccessToken } = useGoogleAuth();

  useEffect(() => {
    if (!isAuthenticated || !user?.email || !metrics?.url) {
      setExistingMapping(null);
      return;
    }
    fetchUserMappings(user.email)
      .then((mappings) =>
        setExistingMapping(findMappingForUrl(mappings, metrics.url))
      )
      .catch(() => setExistingMapping(null));
  }, [isAuthenticated, user?.email, metrics?.url]);

  const getDeviceResults = () => {
    const desktop =
      allMetrics?.desktop || (metrics.device === "desktop" ? metrics : null);
    const mobile =
      allMetrics?.mobile || (metrics.device === "mobile" ? metrics : null);
    return { desktop, mobile };
  };

  useEffect(() => {
    const { desktop, mobile } = getDeviceResults();
    const key = getTestRunKey(metrics, desktop, mobile);
    setShareUrl(key ? getCachedShareUrl(key) : null);
    setLinkCopied(false);
  }, [metrics, allMetrics]);

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
    };
  }, []);

  const showCopiedFeedback = () => {
    setLinkCopied(true);
    if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
    copiedTimeoutRef.current = setTimeout(() => setLinkCopied(false), 2000);
  };

  const openTrackFlow = () => {
    if (!isAuthenticated) {
      openAuthModal("auth");
      return;
    }
    setTrackModalOpen(true);
  };

  const quickSave = async () => {
    if (!existingMapping) {
      openTrackFlow();
      return;
    }
    if (!googleConnected || !googleAccessToken) {
      toast.info("Connect Google to save. Opening setup.");
      openTrackFlow();
      return;
    }

    setSaveLoading(true);
    try {
      const toSave = [];
      if (allMetrics?.desktop) toSave.push(allMetrics.desktop);
      if (allMetrics?.mobile) toSave.push(allMetrics.mobile);
      if (!toSave.length) toSave.push(metrics);

      for (const m of toSave) {
        await saveMetricsToUserSheet(
          m,
          existingMapping.sheet_id,
          existingMapping.tab_name || "Sheet1"
        );
      }
      trackEvent(AnalyticsEvents.SHEET_SAVE, { url: metrics.url });
      toast.success("Saved to Google Sheet!");
    } catch (err) {
      toast.error(formatSheetSaveError(err));
      setTrackModalOpen(true);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCreateShareLink = async () => {
    const { desktop, mobile } = getDeviceResults();

    if (!desktop && !mobile) {
      toast.error("Run both desktop and mobile tests for a full share link.");
      return;
    }

    const cacheKey = getTestRunKey(metrics, desktop, mobile);
    const cached = cacheKey ? getCachedShareUrl(cacheKey) : null;

    if (cached) {
      setShareUrl(cached);
      const copied = await copyShareUrl(cached);
      if (copied) showCopiedFeedback();
      return;
    }

    setShareLoading(true);
    try {
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/api/reports`,
        { url: metrics.url, desktop, mobile },
        { headers }
      );

      const shareLink = res.data?.url;
      if (!shareLink) {
        toast.error("Share link was created but the URL was missing.");
        return;
      }

      if (cacheKey) setCachedShareUrl(cacheKey, shareLink);
      setShareUrl(shareLink);

      const copied = await copyShareUrl(shareLink);
      if (copied) showCopiedFeedback();
      trackEvent(AnalyticsEvents.SHARE_REPORT, { url: metrics.url });
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to create share link.");
    } finally {
      setShareLoading(false);
    }
  };

  const hasTracking =
    existingMapping && isAuthenticated && googleConnected;

  return (
    <>
      {existingMapping && isAuthenticated && (
        <p className="text-center text-xs text-muted-foreground mb-4">
          Tracking in{" "}
          {existingMapping.sheet_id ? (
            <a
              href={`https://docs.google.com/spreadsheets/d/${existingMapping.sheet_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-brand hover:text-brand/80 hover:underline"
            >
              {existingMapping.sheet_name || "Google Sheet"}
            </a>
          ) : (
            <span className="font-medium text-foreground">
              {existingMapping.sheet_name || "Google Sheet"}
            </span>
          )}
          {" · "}
          <button
            type="button"
            onClick={openTrackFlow}
            className="text-brand hover:text-brand/80 hover:underline"
          >
            Change
          </button>
        </p>
      )}

      <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-center gap-2 sm:gap-3 w-full sm:w-auto">
        {hasTracking ? (
          <button
            onClick={quickSave}
            disabled={saveLoading}
            className="inline-flex items-center justify-center gap-2 h-10 w-full sm:w-auto sm:min-w-[140px] px-5 rounded-lg border border-transparent bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 disabled:opacity-60 shadow-sm"
          >
            <Table size={17} />
            {saveLoading ? "Saving…" : "Save to Sheet"}
          </button>
        ) : (
          <button
            onClick={openTrackFlow}
            className="inline-flex items-center justify-center gap-2 h-10 w-full sm:w-auto sm:min-w-[140px] px-5 rounded-lg border border-transparent bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 shadow-sm"
          >
            <Table size={17} />
            Track in Sheets
          </button>
        )}

        <button
          onClick={handleCreateShareLink}
          disabled={shareLoading}
          className="inline-flex items-center justify-center gap-2 h-10 w-full sm:w-auto sm:min-w-[140px] px-5 rounded-lg border border-border bg-card text-brand text-sm font-medium hover:bg-muted/60 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
        >
          {linkCopied ? (
            <Check size={17} />
          ) : shareUrl ? (
            <Copy size={17} />
          ) : (
            <Link2 size={17} />
          )}
          {shareLoading
            ? "Creating…"
            : linkCopied
              ? "Copied"
              : shareUrl
                ? "Copy Link"
                : "Share report"}
        </button>
      </div>

      {isAuthenticated && (
        <div className="flex justify-center mt-3 text-xs text-muted-foreground">
          <Link
            to="/dashboard?tab=mappings"
            className="inline-flex items-center gap-1 hover:text-foreground hover:underline"
          >
            <Settings2 size={13} />
            All mappings
          </Link>
        </div>
      )}

      <TrackInSheetsModal
        isOpen={trackModalOpen}
        onClose={() => setTrackModalOpen(false)}
        url={metrics.url}
        metrics={metrics}
        allMetrics={allMetrics}
        onTracked={() => {
          if (user?.email) {
            fetchUserMappings(user.email).then((m) =>
              setExistingMapping(findMappingForUrl(m, metrics.url))
            );
          }
        }}
      />
    </>
  );
};

export default MetricActions;
